import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS } from '../firebase/collections';
import { mapUserDoc } from '../utils/userMapper';
import { mapFirestoreDoc } from '../utils/mapFirestoreDoc';
import { toISOString } from '../utils/timestamp';
import { debugLog } from '../utils/debugLog';
import {
  normalizePromoCode,
  resolvePromoDuration,
} from './promoCodeService';

const DEFAULT_CATEGORIES = {
  interests: [
    { id: 1, name: 'Sports', icon: '⚽', status: 'active' },
    { id: 2, name: 'Music', icon: '🎵', status: 'active' },
    { id: 3, name: 'Travel', icon: '✈️', status: 'active' },
    { id: 4, name: 'Food', icon: '🍕', status: 'active' },
    { id: 5, name: 'Technology', icon: '💻', status: 'active' },
    { id: 6, name: 'Fitness', icon: '💪', status: 'active' },
    { id: 7, name: 'Art', icon: '🎨', status: 'active' },
    { id: 8, name: 'Gaming', icon: '🎮', status: 'active' },
  ],
  positions: [
    { id: 1, name: 'Top', icon: '⬆️', status: 'active' },
    { id: 2, name: 'Bottom', icon: '⬇️', status: 'active' },
    { id: 3, name: 'Versatile', icon: '🔄', status: 'active' },
    { id: 4, name: 'Side', icon: '↔️', status: 'active' },
  ],
  relationships: [
    { id: 1, name: 'Dating', icon: '💕', status: 'active' },
    { id: 2, name: 'Friends', icon: '👥', status: 'active' },
    { id: 3, name: 'Networking', icon: '🤝', status: 'active' },
    { id: 4, name: 'Long-term', icon: '💍', status: 'active' },
    { id: 5, name: 'Casual', icon: '😊', status: 'active' },
  ],
  tabs: [
    { id: 1, name: 'Browse', icon: '🔍', route: '/browse', status: 'active' },
    { id: 2, name: 'Chat', icon: '💬', route: '/chat', status: 'active' },
    { id: 3, name: 'Profile', icon: '👤', route: '/profile', status: 'active' },
    { id: 4, name: 'Settings', icon: '⚙️', route: '/settings', status: 'active' },
  ],
  identityPreferences: [
    {
      id: 1,
      label: 'I am a:',
      status: 'active',
      options: [
        { id: 1, name: 'Man', status: 'active' },
        { id: 2, name: 'Women', status: 'active' },
        { id: 3, name: 'Others', status: 'active' },
      ],
    },
    {
      id: 2,
      label: 'I am seeking a:',
      status: 'active',
      options: [
        { id: 1, name: 'Man', status: 'active' },
        { id: 2, name: 'Women', status: 'active' },
        { id: 3, name: 'Others', status: 'active' },
      ],
    },
    {
      id: 3,
      label: 'Sexual Orientation:',
      status: 'active',
      options: [
        { id: 1, name: 'Straight', status: 'active' },
        { id: 2, name: 'Gay', status: 'active' },
        { id: 3, name: 'Bisexual', status: 'active' },
        { id: 4, name: 'Other', status: 'active' },
      ],
    },
  ],
  manageStatus: [
    {
      id: 1,
      label: 'Age',
      status: 'active',
      options: [
        { id: 1, name: '18-25', status: 'active' },
        { id: 2, name: '26-35', status: 'active' },
        { id: 3, name: '36-45', status: 'active' },
        { id: 4, name: '46+', status: 'active' },
      ],
    },
    {
      id: 2,
      label: 'Height',
      status: 'active',
      options: [
        { id: 1, name: "5'0\" - 5'5\"", status: 'active' },
        { id: 2, name: "5'6\" - 5'11\"", status: 'active' },
        { id: 3, name: "6'0\"+", status: 'active' },
      ],
    },
    {
      id: 3,
      label: 'Weight',
      status: 'active',
      options: [
        { id: 1, name: '50-64 kg', status: 'active' },
        { id: 2, name: '64-72 kg', status: 'active' },
        { id: 3, name: '72-90 kg', status: 'active' },
        { id: 4, name: '90+ kg', status: 'active' },
      ],
    },
    {
      id: 4,
      label: 'Body Type',
      status: 'active',
      options: [
        { id: 1, name: 'Slim', status: 'active' },
        { id: 2, name: 'Average', status: 'active' },
        { id: 3, name: 'Athletic', status: 'active' },
        { id: 4, name: 'Heavy', status: 'active' },
      ],
    },
    {
      id: 5,
      label: 'Ethnicity',
      status: 'active',
      options: [
        { id: 1, name: 'Asian', status: 'active' },
        { id: 2, name: 'Black', status: 'active' },
        { id: 3, name: 'Hispanic', status: 'active' },
        { id: 4, name: 'White', status: 'active' },
        { id: 5, name: 'Mixed', status: 'active' },
        { id: 6, name: 'Other', status: 'active' },
      ],
    },
    {
      id: 6,
      label: 'Relationship Status',
      status: 'active',
      options: [
        { id: 1, name: 'Single', status: 'active' },
        { id: 2, name: 'In a relationship', status: 'active' },
        { id: 3, name: 'Married', status: 'active' },
        { id: 4, name: 'Complicated', status: 'active' },
      ],
    },
    {
      id: 7,
      label: 'Tribe',
      status: 'active',
      options: [
        { id: 1, name: 'Bear', status: 'active' },
        { id: 2, name: 'Cub', status: 'active' },
        { id: 3, name: 'Otter', status: 'active' },
        { id: 4, name: 'Twink', status: 'active' },
        { id: 5, name: 'Jock', status: 'active' },
        { id: 6, name: 'Other', status: 'active' },
      ],
    },
  ],
};

const subscriptionsMapFromSnapshot = (snapshot) => {
  const map = {};
  snapshot.forEach((subDoc) => {
    const data = subDoc.data();
    const record = { id: subDoc.id, ...data };
    map[subDoc.id] = record;
    if (data.userId) map[data.userId] = record;
    if (data.uid && data.uid !== data.userId) map[data.uid] = record;
  });
  return map;
};

export const fetchSubscriptionsMap = async () => {
  const snapshot = await getDocs(collection(db, COLLECTIONS.SUBSCRIPTIONS));
  return subscriptionsMapFromSnapshot(snapshot);
};

export const fetchAllUsers = async () => {
  const [usersSnapshot, subsMap] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.USERS)),
    fetchSubscriptionsMap(),
  ]);

  debugLog('Firebase', 'users/subscriptions fetched', {
    usersCount: usersSnapshot.size,
    subscriptionsCount: Object.keys(subsMap).length,
  });

  const users = usersSnapshot.docs.map((userDoc) =>
    mapUserDoc(userDoc.id, userDoc.data(), subsMap[userDoc.id])
  );

  const missingSub = users.filter((user) => !subsMap[user.uid]).length;
  debugLog('Firebase', 'mapped users', {
    mappedCount: users.length,
    usersWithoutSubscriptionDoc: missingSub,
    isActiveTrue: users.filter((user) => user.isActive === true).length,
    mappedPremium: users.filter((user) => user.subscriptionType === 'premium').length,
  });

  return users;
};

export const fetchUserById = async (uid) => {
  const [userSnap, subSnap] = await Promise.all([
    getDoc(doc(db, COLLECTIONS.USERS, uid)),
    getDoc(doc(db, COLLECTIONS.SUBSCRIPTIONS, uid)),
  ]);

  if (!userSnap.exists()) return null;
  const subscription = subSnap.exists() ? { id: subSnap.id, ...subSnap.data() } : null;
  return mapUserDoc(userSnap.id, userSnap.data(), subscription);
};

export const updateUser = async (uid, updates) => {
  const { subscriptionType, subscriptionExpiry, autoRenewal, promoCodeUsed, ...userUpdates } = updates;

  const userRef = doc(db, COLLECTIONS.USERS, uid);
  await updateDoc(userRef, {
    ...userUpdates,
    updatedAt: serverTimestamp(),
  });

  const hasSubscriptionUpdate =
    subscriptionType !== undefined ||
    subscriptionExpiry !== undefined ||
    autoRenewal !== undefined ||
    promoCodeUsed !== undefined;

  if (hasSubscriptionUpdate) {
    const subRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, uid);
    const subSnap = await getDoc(subRef);
    const subData = {
      userId: uid,
      type: subscriptionType,
      subscriptionType,
      expiryDate: subscriptionExpiry || null,
      subscriptionExpiry: subscriptionExpiry || null,
      autoRenewal: autoRenewal ?? false,
      updatedAt: serverTimestamp(),
    };
    if (subscriptionType !== undefined) {
      subData.isActive = subscriptionType === 'premium';
    }
    if (promoCodeUsed) subData.promoCodeUsed = promoCodeUsed;

    if (subSnap.exists()) {
      await updateDoc(subRef, subData);
    } else {
      await setDoc(subRef, {
        ...subData,
        createdAt: serverTimestamp(),
      });
    }
  }
};

export const updateUserByUsername = async (username, updates) => {
  const users = await fetchAllUsers();
  const user = users.find((u) => u.username === username);
  if (!user) throw new Error('User not found');
  await updateUser(user.uid, updates);
  return user.uid;
};

export const softDeleteUser = async (uid) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
    status: 'deleted',
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const restoreUser = async (uid) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
    status: 'active',
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
};

export const permanentDeleteUser = async (uid, username) => {
  const batch = writeBatch(db);

  batch.delete(doc(db, COLLECTIONS.USERS, uid));
  batch.delete(doc(db, COLLECTIONS.SUBSCRIPTIONS, uid));

  if (username) {
    batch.delete(doc(db, COLLECTIONS.USERNAMES, username.toLowerCase()));
  }

  const relatedCollections = [
    COLLECTIONS.BLOCKS,
    COLLECTIONS.CONTACTS,
    COLLECTIONS.FAVORITES,
    COLLECTIONS.PRESENCE,
    COLLECTIONS.PROFILE_CARDS,
    COLLECTIONS.TAPS,
  ];

  const userIdFields = ['userId', 'uid', 'userUid', 'viewerId', 'fromUserId', 'blockerId', 'ownerId'];

  for (const collName of relatedCollections) {
    for (const field of userIdFields) {
      const snap = await getDocs(query(collection(db, collName), where(field, '==', uid)));
      snap.forEach((d) => batch.delete(d.ref));
    }
  }

  for (const collName of [COLLECTIONS.CONVERSATIONS, COLLECTIONS.ALBUMS, COLLECTIONS.PROFILE_IMAGE_VIEWS]) {
    for (const field of ['userId', 'uid', 'userUid', 'participantId', 'viewedUserId', 'toUserId', 'blockedId']) {
      const snap = await getDocs(query(collection(db, collName), where(field, '==', uid)));
      snap.forEach((d) => batch.delete(d.ref));
    }
  }

  await batch.commit();
};

const CATEGORY_GROUPS = ['interests', 'positions', 'relationships', 'tabs', 'identityPreferences', 'manageStatus'];

export const fetchCategories = async () => {
  const snaps = await Promise.all(
    CATEGORY_GROUPS.map((group) => getDoc(doc(db, COLLECTIONS.APP_CATEGORIES, group)))
  );

  const hasAny = snaps.some((snap) => snap.exists());

  if (!hasAny) {
    await saveCategories(DEFAULT_CATEGORIES);
    return DEFAULT_CATEGORIES;
  }

  const result = {};
  let shouldSeedDefaults = false;

  CATEGORY_GROUPS.forEach((group, index) => {
    const snap = snaps[index];
    if ((group === 'identityPreferences' || group === 'manageStatus') && !snap.exists()) {
      result[group] = DEFAULT_CATEGORIES[group];
      shouldSeedDefaults = true;
      return;
    }
    result[group] = snap.exists() ? snap.data().items || [] : [];
  });

  if (shouldSeedDefaults) {
    await saveCategories(result);
  }

  return result;
};

export const saveCategories = async (categories) => {
  const snapshot = await getDocs(collection(db, COLLECTIONS.APP_CATEGORIES));

  const batch = writeBatch(db);

  CATEGORY_GROUPS.forEach((group) => {
    batch.set(doc(db, COLLECTIONS.APP_CATEGORIES, group), {
      items: categories[group] || [],
      updatedAt: serverTimestamp(),
    });
  });

  snapshot.forEach((d) => {
    if (!CATEGORY_GROUPS.includes(d.id)) {
      batch.delete(d.ref);
    }
  });

  await batch.commit();
};

const durationToDays = (duration) => {
  if (!duration || duration.unit === 'lifetime') return 36500;
  const value = Number(duration.value) || 0;
  switch (duration.unit) {
    case 'weeks':
      return value * 7;
    case 'months':
      return value * 30;
    case 'years':
      return value * 365;
    case 'days':
    default:
      return value;
  }
};

const mapPromoDoc = (d) => {
  const data = d.data() || {};
  const duration = resolvePromoDuration(data);
  const durationDays =
    data.durationDays != null
      ? Number(data.durationDays)
      : durationToDays(duration);

  return {
    id: d.id,
    code: normalizePromoCode(data.code || d.id),
    description: data.description || '',
    duration: { value: durationDays, unit: 'days' },
    durationValue: durationDays,
    durationUnit: 'days',
    durationDays,
    maxUses: data.maxUses ?? data.max_uses ?? null,
    usedCount:
      data.usedCount ?? data.used_count ?? data.redemptionCount ?? data.currentUses ?? 0,
    status: data.status || (data.isActive === false ? 'inactive' : 'active'),
    expiryDate: toISOString(data.expiryDate || data.expiresAt || data.expiry),
    createdAt: toISOString(data.createdAt),
    updatedAt: toISOString(data.updatedAt),
    collectionName: d.ref?.parent?.id || COLLECTIONS.PROMO_CODES,
  };
};

const toPromoTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === 'function') return value;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Timestamp.fromDate(date);
};

const sortPromoCodes = (codes) =>
  codes.sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
  );

export const resolvePromoCollection = async () => COLLECTIONS.PROMO_CODES;

export const fetchPromoCodes = async () => {
  const collectionName = COLLECTIONS.PROMO_CODES;
  const snapshot = await getDocs(collection(db, collectionName));
  return sortPromoCodes(
    snapshot.docs.map((d) => ({
      ...mapPromoDoc(d),
      collectionName,
    }))
  );
};

export const savePromoCode = async (codeData) => {
  const collectionName = COLLECTIONS.PROMO_CODES;
  const normalizedCode = normalizePromoCode(codeData.code);
  const duration = resolvePromoDuration({
    duration: codeData.duration,
    durationValue: codeData.durationValue,
    durationUnit: codeData.durationUnit,
    durationDays: codeData.durationDays,
    lifetime: codeData.lifetime || codeData.durationUnit === 'lifetime',
  });

  if (!normalizedCode) {
    throw new Error('Promo code is required.');
  }

  const durationDays = durationToDays(duration);
  if (!durationDays || durationDays < 1) {
    throw new Error('Promo duration is required.');
  }

  const id = codeData.id ? String(codeData.id) : undefined;
  // Match existing promo_codes docs: auto id, not code-as-id.
  const ref = id ? doc(db, collectionName, id) : doc(collection(db, collectionName));

  const payload = {
    code: normalizedCode,
    description: codeData.description || '',
    durationDays,
    maxUses:
      codeData.maxUses === undefined || codeData.maxUses === null || codeData.maxUses === ''
        ? null
        : Number(codeData.maxUses),
    expiryDate:
      codeData.expiryDate === undefined
        ? null
        : toPromoTimestamp(codeData.expiryDate),
    status: codeData.status || 'active',
    updatedAt: serverTimestamp(),
  };

  if (id) {
    await setDoc(ref, payload, { merge: true });
    return id;
  }

  await setDoc(ref, {
    ...payload,
    usedCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const deletePromoCode = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.PROMO_CODES, String(id)));
};

export const addAuditLog = async (log) => {
  const ref = doc(collection(db, COLLECTIONS.AUDIT_LOGS));
  await setDoc(ref, {
    ...log,
    timestamp: serverTimestamp(),
  });
};

export const fetchAuditLogs = async (filters = {}) => {
  let q = query(collection(db, COLLECTIONS.AUDIT_LOGS), orderBy('timestamp', 'desc'), limit(1000));

  try {
    const snapshot = await getDocs(q);
    let logs = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      timestamp: toISOString(d.data().timestamp) || new Date().toISOString(),
    }));

    if (filters.startDate) {
      logs = logs.filter((log) => new Date(log.timestamp) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      logs = logs.filter((log) => new Date(log.timestamp) <= new Date(filters.endDate));
    }
    if (filters.adminId) {
      logs = logs.filter((log) => log.adminId === filters.adminId);
    }
    if (filters.action) {
      logs = logs.filter((log) =>
        log.action?.toLowerCase().includes(filters.action.toLowerCase())
      );
    }

    return logs;
  } catch {
    const snapshot = await getDocs(collection(db, COLLECTIONS.AUDIT_LOGS));
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      timestamp: toISOString(d.data().timestamp) || new Date().toISOString(),
    }));
  }
};

export const fetchAdmins = async () => {
  const snapshot = await getDocs(collection(db, COLLECTIONS.ADMINS));
  return snapshot.docs.map((d) => ({
    id: d.id,
    uid: d.id,
    ...d.data(),
    createdAt: toISOString(d.data().createdAt),
    lastLogin: toISOString(d.data().lastLogin),
  }));
};

export const fetchAdminByUid = async (uid) => {
  const snap = await getDoc(doc(db, COLLECTIONS.ADMINS, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, uid: snap.id, ...snap.data() };
};

export const fetchAdminByEmail = async (email) => {
  const q = query(collection(db, COLLECTIONS.ADMINS), where('email', '==', email), limit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, uid: d.id, ...d.data() };
};

export const updateAdminProfile = async (uid, updates) => {
  await updateDoc(doc(db, COLLECTIONS.ADMINS, uid), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const createAdminDoc = async (uid, data) => {
  await setDoc(doc(db, COLLECTIONS.ADMINS, uid), {
    ...data,
    status: 'active',
    createdAt: serverTimestamp(),
  });
};

export const fetchAdminSettings = async (uid) => {
  const snap = await getDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, uid));
  if (!snap.exists()) {
    return {
      emailNotifications: true,
      loginAlerts: true,
      autoLogout: 30,
      twoFactorAuth: false,
    };
  }
  return snap.data();
};

export const saveAdminSettings = async (uid, settings) => {
  await setDoc(
    doc(db, COLLECTIONS.ADMIN_SETTINGS, uid),
    { ...settings, updatedAt: serverTimestamp() },
    { merge: true }
  );
};

export const checkUsernameAvailable = async (username) => {
  const snap = await getDoc(doc(db, COLLECTIONS.USERNAMES, username.toLowerCase()));
  return !snap.exists();
};

export const getEmailByUsername = async (username) => {
  const snap = await getDoc(doc(db, COLLECTIONS.USERNAMES, username.toLowerCase()));
  if (!snap.exists()) return null;
  return snap.data().email;
};

export const createUserProfile = async (uid, userData) => {
  const batch = writeBatch(db);

  batch.set(doc(db, COLLECTIONS.USERS, uid), {
    username: userData.username,
    email: userData.email,
    fullName: userData.fullName || '',
    birthdate: userData.birthdate || null,
    status: 'active',
    subscriptionType: 'free',
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp(),
    ipAddress: userData.ipAddress || '',
  });

  batch.set(doc(db, COLLECTIONS.USERNAMES, userData.username.toLowerCase()), {
    uid,
    email: userData.email,
    username: userData.username,
    createdAt: serverTimestamp(),
  });

  batch.set(doc(db, COLLECTIONS.SUBSCRIPTIONS, uid), {
    userId: uid,
    type: 'free',
    subscriptionType: 'free',
    autoRenewal: false,
    createdAt: serverTimestamp(),
  });

  await batch.commit();
};

export const updateUserLastLogin = async (uid) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
    lastLogin: serverTimestamp(),
  });
};

export const fetchCollectionDocuments = async (collectionName) => {
  let snapshot;

  try {
    snapshot = await getDocs(
      query(collection(db, collectionName), orderBy('createdAt', 'desc'), limit(500))
    );
  } catch {
    try {
      snapshot = await getDocs(
        query(collection(db, collectionName), orderBy('updatedAt', 'desc'), limit(500))
      );
    } catch {
      snapshot = await getDocs(collection(db, collectionName));
    }
  }

  return snapshot.docs.map((d) => mapFirestoreDoc(d.id, d.data()));
};

export const deleteCollectionDocument = async (collectionName, documentId) => {
  await deleteDoc(doc(db, collectionName, documentId));
};

export const fetchCollectionCount = async (collectionName) => {
  const snapshot = await getDocs(collection(db, collectionName));
  return snapshot.size;
};
